/**
 * RemoteRestoreScreen
 *
 * Door B (new device / cleared cache).
 *   1. User types @sigil + PIN
 *   2. Fetch encrypted backup from server
 *   3. Decrypt locally with PIN
 *   4. Derive crypto keys
 *   5. Save locally for future Door A unlocks
 *   6. Go to /inbox
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box, Cursor, FieldLabel } from '../components/Frame';
import { useStore } from '../lib/store';
import { auth, signInAnonymously, callFn } from '../lib/firebase';
import {
  decryptMnemonic,
  saveLastSigil,
  saveUnlockMode,
  resetLocalAttempts,
  isValidPin,
  addAccount,
} from '../lib/pinCrypto';
import { mnemonicToSeed, isValid } from '../lib/mnemonic';
import {
  initSodium, keypairFromSeed, deriveUid,
  boxKeypairFromSeed, toBase64Url,
} from '../lib/crypto';
import { signInAsIdentity } from '../lib/authIdentity';
import { saveKeys } from '../lib/keys';
import { saveSession } from '../lib/storage';
import { publishMyKeysFromKeypairs } from '../lib/messaging';
import { ensureMyPrekeysPublished } from '../lib/fsMessaging';

const SIGIL_RE = /^[a-z0-9_]{3,20}$/i;

export default function RemoteRestoreScreen() {
  const navigate = useNavigate();
  const { setIdentity, setBoxKeys, setFirebaseUser, setSigil } = useStore();

  const [sigilInput, setSigilInput] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [stage, setStage] = useState('idle');

  async function onRestore(e) {
    e?.preventDefault?.();
    setErr(null);
    const cleanSigil = sigilInput.trim().replace(/^@/, '').toLowerCase();
    if (!SIGIL_RE.test(cleanSigil)) {
      setErr('sigil non valido (3-20 char, a-z 0-9 _)');
      return;
    }
    // Segreto: PIN (6 cifre) o passphrase (Premium). Il decrypt lo valida.
    if (pin.length < 6) {
      setErr('inserisci il PIN (6 cifre) o la passphrase');
      return;
    }

    setBusy(true);
    try {
      // 1. Firebase anonymous auth (needed to call the function)
      setStage('auth');
      const cred = await signInAnonymously(auth());
      const firebaseUid = cred.user.uid;

      // 2. Fetch encrypted backup from server
      setStage('fetching');
      const fn = callFn('fetchBackup');
      const res = await fn({ sigilLower: cleanSigil });
      const payload = res.data;

      if (!payload?.ok) {
        if (payload?.rateLimited) {
          setErr(
            `troppi tentativi. Riprova tra ${payload.retryAfterMinutes} minuti, ` +
            'oppure usa "ho dimenticato il PIN" qui sotto.'
          );
        } else {
          setErr(
            `server: ${payload?.error || 'sconosciuto'}` +
            (payload?.stage ? ` (${payload.stage})` : '')
          );
        }
        return;
      }
      if (!payload.found) {
        setErr(
          'sigil non trovato o PIN sbagliato. ' +
          'verifica @sigil. se è quello giusto e il PIN è sbagliato, ' +
          'usa "ho dimenticato il PIN" qui sotto.'
        );
        return;
      }

      // 3. Decrypt locally with the PIN
      setStage('decrypting');
      const mnemonic = await decryptMnemonic({
        pin,
        sigilLower: cleanSigil,
        blob: payload.blob,
        salt: payload.salt,
        nonce: payload.nonce,
        argon2: payload.argon2 || null,
      });
      if (!mnemonic || !isValid(mnemonic)) {
        // Backup exists but PIN doesn't decrypt it
        setErr(
          'PIN sbagliato per questo sigil. ' +
          'se non ricordi il PIN, usa "ho dimenticato il PIN" qui sotto.'
        );
        return;
      }

      // 4. Derive crypto material
      setStage('deriving');
      await initSodium();
      const seed = mnemonicToSeed(mnemonic);
      const signKp = await keypairFromSeed(seed);
      const boxKp = await boxKeypairFromSeed(seed);
      const cryptoUid = await deriveUid(signKp.publicKey);

      // 4b. Auth a token custom: l'anon effimero (step 1) è servito solo per
      //     fetchBackup. Ora passiamo alla sessione DELL'IDENTITÀ (uid =
      //     deriveUid(pubKeySign) = cryptoUid), mai condivisa → scollegabile.
      setStage('auth identità');
      await signInAsIdentity({ signPub: signKp.publicKey, signSec: signKp.secretKey });

      // 5. Hydrate store
      setIdentity({
        publicKey: signKp.publicKey,
        secretKey: signKp.secretKey,
        uid: cryptoUid,
        mnemonic,
      });
      setBoxKeys?.({ boxPub: boxKp.publicKey, boxSec: boxKp.secretKey });
      setFirebaseUser(auth().currentUser);

      // Il sigil viene salvato in minuscolo; la UI mostra @sigilLower finché al
      // reload /users/{uid}.sigil non popola il caso originale (cosmetico).
      setSigil(cleanSigil);

      // Persisti il sigil + la modalità di sblocco (veloci, localStorage): il
      // blob resta autoritativo sul server (rate-limited), niente blob in locale.
      try { saveLastSigil(cleanSigil); } catch (e) { console.warn('saveLastSigil', e); }
      try { addAccount(cleanSigil, { tor: false }); } catch {} // multi-identità: nel catalogo del device
      try { saveUnlockMode(cleanSigil, isValidPin(pin) ? 'pin' : 'passphrase'); } catch {}
      resetLocalAttempts();

      // Vai SUBITO all'inbox (store già idratato): rete + IDB non bloccano.
      navigate('/inbox', { replace: true });

      // In BACKGROUND: lega l'uid al sigil (questo è un NUOVO device per
      // l'identità → multi-device, il server lo aggiunge a sigils/{sigil}.devices),
      // ripubblica le chiavi pubbliche e persiste su IDB. Errori non bloccanti.
      toBase64Url(signKp.publicKey)
        .then((pub) => callFn('registerSigil')({ sigil: cleanSigil, pubKeySign: pub }))
        .catch((e) => console.warn('registerSigil (restore)', e));
      publishMyKeysFromKeypairs(signKp.publicKey, boxKp.publicKey)
        .catch((e) => console.warn('publishKeys (restore)', e));
      // Forward Secrecy: pubblica le prekey su questo (nuovo) device.
      ensureMyPrekeysPublished({
        signKeypair: { pub: signKp.publicKey, priv: signKp.secretKey },
        sigil: cleanSigil,
      }).catch((e) => console.warn('prekeys (restore)', e));
      saveKeys({
        mnemonic,
        uid: cryptoUid,
        sigil: cleanSigil,
        signPub: signKp.publicKey,
        signSec: signKp.secretKey,
        boxPub: boxKp.publicKey,
        boxSec: boxKp.secretKey,
        securityLevel: 1,
      }).catch((e) => console.warn('saveKeys', e));
      try {
        saveSession({ mnemonic, uid: cryptoUid, sigil: cleanSigil });
      } catch (e) { console.warn('saveSession', e); }
    } catch (e) {
      console.error('remote restore failed', e);
      setErr(e?.message || 'errore sconosciuto');
    } finally {
      setBusy(false);
      setStage('idle');
    }
  }

  return (
    <Frame statusLeft="RESTORE :: SIGIL+PIN" statusRight="5G">
      <Header
        title="// Accedi"
        crumb="◀ indietro"
        onCrumbClick={() => navigate('/welcome')}
      />

      <p className="text-[16px] text-ink-dim mb-4">
        inserisci il tuo sigil e il PIN (o la passphrase, se l'hai impostata).
        Nullum scarica il backup cifrato e lo apre qui sul dispositivo.
      </p>

      <form onSubmit={onRestore} className="space-y-3">
        <div>
          <FieldLabel>&gt; SIGIL</FieldLabel>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none select-none text-[16px]">@</span>
            <input
              type="text"
              name="sigil"
              autoComplete="off"
              spellCheck="false"
              value={sigilInput}
              onChange={(e) => setSigilInput(e.target.value)}
              placeholder="iltuonome"
              disabled={busy}
              className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] pl-7 pr-3 py-2 focus:border-ink focus:outline-none"
            />
          </div>
        </div>

        <div>
          <FieldLabel>&gt; PIN O PASSPHRASE</FieldLabel>
          <input
            type="password"
            autoComplete="current-password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="il tuo PIN o la passphrase"
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] px-3 py-2 focus:border-ink focus:outline-none"
          />
        </div>

        {err && (
          <Box variant="danger" className="text-[16px]">
            <span className="text-red-500">✕</span> {err}
          </Box>
        )}

        {busy && (
          <Box variant="warn" className="text-[16px]">
            {stage === 'auth' && (<>autenticazione<Cursor /></>)}
            {stage === 'fetching' && (<>scarico backup<Cursor /></>)}
            {stage === 'decrypting' && (<>decifrando (Argon2id)<Cursor /></>)}
            {stage === 'deriving' && (<>derivando chiavi<Cursor /></>)}
          </Box>
        )}

        <Btn
          type="submit"
          variant="primary"
          disabled={busy || !sigilInput.trim() || pin.length < 6}
        >
          {busy ? '...' : 'Sblocca ▶'}
        </Btn>
      </form>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/recover')}>
          Ho dimenticato il PIN
        </Btn>
      </Footer>
    </Frame>
  );
}
